import type { CalendarEvent } from '@/pages/calendar/utils/eventMapper';

import { MeetingsNeedingNotesList } from './MeetingsNeedingNotesList';

export interface WeeklyRecapViewProps {
  weeklyActionsCreated: number;
  weeklyActionsApproved: number;
  /** When true (page size cap reached), append '+' to the created count. */
  mayUndercountCreated?: boolean;
  meetingsNeedingNotes: CalendarEvent[];
  timeZone?: string;
  /** Undefined when the user lacks the ahoy_note create grant — the meetings
   *  list then renders no "Add summary" button. */
  onAddNote?: (meetingEntityId: string) => void;
  onShowAllActions: () => void;
}

function WeeklyRecapHeader({
  onShowAllActions,
}: {
  onShowAllActions: () => void;
}) {
  return (
    <div className='flex items-center justify-between mt-0.5 mb-3'>
      <h2 className='text-[15px] font-semibold text-[var(--color-text-primary)] dark:text-neutral-100'>
        Week in review
      </h2>
      <button
        type='button'
        onClick={onShowAllActions}
        className='text-[12px] font-semibold text-black dark:text-neutral-100 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors'
      >
        Show all pending actions
      </button>
    </div>
  );
}

interface StatTileProps {
  label: string;
  value: string;
}

function StatTile({ label, value }: StatTileProps) {
  return (
    <div className='flex flex-col items-start px-4 py-3 rounded-[4px] border border-secondary bg-white dark:bg-neutral-900'>
      <p className='text-[20px] font-semibold text-[var(--color-text-primary)]'>
        {value}
      </p>
      <p className='text-[12px] text-[var(--color-text-secondary)]'>{label}</p>
    </div>
  );
}

export function WeeklyRecapView({
  weeklyActionsCreated,
  weeklyActionsApproved,
  mayUndercountCreated = false,
  meetingsNeedingNotes,
  timeZone,
  onAddNote,
  onShowAllActions,
}: WeeklyRecapViewProps) {
  const createdLabel = `${weeklyActionsCreated}${mayUndercountCreated ? '+' : ''}`;
  return (
    <div className='space-y-4'>
      <WeeklyRecapHeader onShowAllActions={onShowAllActions} />
      <div className='grid grid-cols-2 gap-2'>
        <StatTile label='Actions created this week' value={createdLabel} />
        <StatTile
          label='Actions approved this week'
          value={String(weeklyActionsApproved)}
        />
      </div>
      <MeetingsNeedingNotesList
        meetings={meetingsNeedingNotes}
        onAddNote={onAddNote}
        timeZone={timeZone}
        heading="Today's meetings needing notes"
      />
    </div>
  );
}
